/**
 * Passe-plat pour le fichier déposé sur une landing.
 *
 * Une landing est une page publique : le visiteur y dépose son document
 * AVANT d'avoir un compte. Il faut donc transporter son fichier jusqu'à
 * /upload, de l'autre côté de l'inscription.
 *
 * Pourquoi une variable de module et pas sessionStorage : sessionStorage
 * ne stocke que des chaînes, il faudrait encoder le fichier en base64
 * (+33 %) et un PDF de 20 Mo dépasserait largement le quota de 5 à 10 Mo
 * des navigateurs — l'écriture lèverait, et on perdrait le fichier
 * précisément sur les gros documents. Landing → /signup → /upload est
 * une navigation client (React Router, aucun rechargement), donc le
 * module reste vivant tout du long et porte l'objet `File` intact,
 * quelle que soit sa taille.
 *
 * Contrepartie assumée : un rechargement complet au milieu du parcours
 * (F5, retour depuis un onglet fermé) perd le fichier. /upload retombe
 * alors sur son sélecteur habituel — le visiteur re-choisit son
 * document, il n'est jamais bloqué.
 */

let pending = null;

/** Formats et taille annoncés par la dropzone des landings. */
export const ACCEPTED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
export const MAX_BYTES = 20 * 1024 * 1024;

/**
 * Valide un fichier déposé. Retourne `null` si tout va bien, sinon le
 * message à afficher — en français, à destination du visiteur.
 */
export function validatePendingFile(file) {
  if (!file) return 'Aucun fichier reçu.';
  const ext = String(file.name || '').split('.').pop().toLowerCase();
  if (!ACCEPTED_EXTENSIONS.includes(ext)) {
    return 'Format non accepté. Dépose un PDF, un JPG ou un PNG.';
  }
  if (file.size > MAX_BYTES) {
    return 'Fichier trop lourd — 20 Mo maximum.';
  }
  // Un fichier de 0 octet ne part jamais : le serveur le refuse aussi
  // (400 `document_vide`), mais autant le dire avant l'envoi.
  if (file.size === 0) {
    return 'Ton fichier est vide (0 octet). Redépose-le.';
  }
  return null;
}

/** Mémorise le fichier et le thème de la landing d'où il vient. */
export function setPendingUpload(file, meta = {}) {
  pending = { file, meta, at: Date.now() };
}

/**
 * Récupère le fichier en attente ET l'oublie : on ne veut pas qu'il
 * ressurgisse sur un upload suivant dans la même session.
 */
export function takePendingUpload() {
  const current = pending;
  pending = null;
  return current;
}

/** Y a-t-il un fichier en attente ? (sans le consommer) */
export function hasPendingUpload() {
  return pending !== null;
}
